import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
    Plus,
    Trash2,
    Edit2,
    Key,
    CheckCircle2,
    XCircle,
    AlertCircle,
    Loader2,
    TestTube,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    Truck,
    Brain,
    MapPin,
    MessageSquare,
    Mail,
    Radio,
    MoreHorizontal
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
    load_board: {
        label: 'Load Boards',
        icon: Truck,
        color: 'text-blue-400',
        bgColor: 'bg-blue-500/10',
        borderColor: 'border-blue-500/30',
        providers: ['DAT', 'Truckstop', '123Loadboard', 'TruckSmarter', 'Convoy', 'Uber Freight', 'Custom']
    },
    ai: {
        label: 'AI Services',
        icon: Brain,
        color: 'text-purple-400',
        bgColor: 'bg-purple-500/10',
        borderColor: 'border-purple-500/30',
        providers: ['OpenAI', 'Anthropic', 'Google AI', 'Custom']
    },
    routing: {
        label: 'Routing & Maps',
        icon: MapPin,
        color: 'text-green-400',
        bgColor: 'bg-green-500/10',
        borderColor: 'border-green-500/30',
        providers: ['Google Maps', 'HERE', 'PC Miler', 'Trimble', 'Custom']
    },
    sms: {
        label: 'SMS & Messaging',
        icon: MessageSquare,
        color: 'text-cyan-400',
        bgColor: 'bg-cyan-500/10',
        borderColor: 'border-cyan-500/30',
        providers: ['Twilio', 'Vonage', 'MessageBird', 'Custom']
    },
    gps: {
        label: 'GPS & ELD',
        icon: Radio,
        color: 'text-amber-400',
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/30',
        providers: ['Motive', 'Samsara', 'KeepTruckin', 'Geotab', 'Custom']
    },
    email: {
        label: 'Email Services',
        icon: Mail,
        color: 'text-red-400',
        bgColor: 'bg-red-500/10',
        borderColor: 'border-red-500/30',
        providers: ['Resend', 'SendGrid', 'Mailgun', 'AWS SES', 'Custom']
    },
    other: {
        label: 'Other',
        icon: MoreHorizontal,
        color: 'text-zinc-400',
        bgColor: 'bg-zinc-500/10',
        borderColor: 'border-zinc-500/30',
        providers: ['Custom']
    }
};

const HEALTH_STATUS_CONFIG = {
    healthy: { icon: CheckCircle2, color: 'text-green-400', label: 'Healthy' },
    failed: { icon: XCircle, color: 'text-red-400', label: 'Failed' },
    unknown: { icon: AlertCircle, color: 'text-zinc-400', label: 'Not Tested' }
};

export default function IntegrationRegistry() {
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedCategories, setExpandedCategories] = useState({});
    
    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCredentialsModal, setShowCredentialsModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [testingId, setTestingId] = useState(null);
    const [togglingId, setTogglingId] = useState(null);
    
    // Form states
    const [formData, setFormData] = useState({
        category: '',
        provider_name: '',
        display_name: '',
        api_key: '',
        api_secret: '',
        is_enabled: false,
        notes: '',
        // Email-specific fields
        email_mode: 'draft',  // 'draft' or 'api'
        from_email: '',
        reply_to_email: ''
    });
    const [formSaving, setFormSaving] = useState(false);

    useEffect(() => {
        fetchIntegrations();
    }, []);

    const fetchIntegrations = async () => {
        try {
            const response = await fetch(`${API_URL}/api/integrations`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setIntegrations(data);
                
                // Auto-expand categories that have integrations
                const categoriesWithIntegrations = {};
                data.forEach(int => {
                    categoriesWithIntegrations[int.category] = true;
                });
                setExpandedCategories(categoriesWithIntegrations);
            }
        } catch (error) {
            console.error('Failed to fetch integrations:', error);
            toast.error('Failed to load integrations');
        } finally {
            setLoading(false);
        }
    };

    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const handleToggleEnabled = async (integration) => {
        setTogglingId(integration.integration_id);
        try {
            const response = await fetch(
                `${API_URL}/api/integrations/${integration.integration_id}/enable?enabled=${!integration.is_enabled}`,
                {
                    method: 'PATCH',
                    credentials: 'include'
                }
            );
            if (response.ok) {
                setIntegrations(prev =>
                    prev.map(int =>
                        int.integration_id === integration.integration_id
                            ? { ...int, is_enabled: !int.is_enabled }
                            : int
                    )
                );
                toast.success(`Integration ${!integration.is_enabled ? 'enabled' : 'disabled'}`);
            }
        } catch (error) {
            console.error('Failed to toggle integration:', error);
            toast.error('Failed to update integration');
        } finally {
            setTogglingId(null);
        }
    };

    const handleTestConnection = async (integration) => {
        setTestingId(integration.integration_id);
        try {
            const response = await fetch(
                `${API_URL}/api/integrations/${integration.integration_id}/test`,
                {
                    method: 'POST',
                    credentials: 'include'
                }
            );
            if (response.ok) {
                const result = await response.json();
                setIntegrations(prev =>
                    prev.map(int =>
                        int.integration_id === integration.integration_id
                            ? {
                                ...int,
                                health_status: result.health_status,
                                last_tested_at: result.tested_at,
                                last_error: result.last_error
                            }
                            : int
                    )
                );
                if (result.health_status === 'healthy') {
                    toast.success('Connection test passed');
                } else {
                    toast.error(result.last_error || 'Connection test failed');
                }
            }
        } catch (error) {
            console.error('Failed to test connection:', error);
            toast.error('Failed to test connection');
        } finally {
            setTestingId(null);
        }
    };

    const openAddModal = (category = '') => {
        setFormData({
            category: category,
            provider_name: '',
            display_name: '',
            api_key: '',
            api_secret: '',
            is_enabled: false,
            notes: '',
            email_mode: 'draft',
            from_email: '',
            reply_to_email: ''
        });
        setShowAddModal(true);
    };

    const openEditModal = (integration) => {
        setSelectedIntegration(integration);
        const additionalConfig = integration.additional_config || {};
        setFormData({
            category: integration.category,
            provider_name: integration.provider_name,
            display_name: integration.display_name || '',
            api_key: '',
            api_secret: '',
            is_enabled: integration.is_enabled,
            notes: integration.notes || '',
            // Email-specific fields
            email_mode: additionalConfig.mode || 'draft',
            from_email: additionalConfig.from_email || '',
            reply_to_email: additionalConfig.reply_to_email || ''
        });
        setShowEditModal(true);
    };

    const openCredentialsModal = (integration) => {
        setSelectedIntegration(integration);
        setFormData({
            ...formData,
            api_key: '',
            api_secret: ''
        });
        setShowCredentialsModal(true);
    };

    const openDeleteModal = (integration) => {
        setSelectedIntegration(integration);
        setShowDeleteModal(true);
    };

    const handleAddIntegration = async () => {
        if (!formData.category || !formData.provider_name) {
            toast.error('Category and Provider are required');
            return;
        }

        // Validation for email integrations in API mode
        if (formData.category === 'email' && formData.email_mode === 'api') {
            if (!formData.api_key) {
                toast.error('API Key is required for API mode');
                return;
            }
            if (!formData.from_email) {
                toast.error('From Email is required for API mode');
                return;
            }
        }

        setFormSaving(true);
        try {
            // Build additional_config for email category
            let additional_config = null;
            if (formData.category === 'email') {
                additional_config = {
                    mode: formData.email_mode,
                    from_email: formData.from_email || null,
                    reply_to_email: formData.reply_to_email || null
                };
            }

            const response = await fetch(`${API_URL}/api/integrations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    category: formData.category,
                    provider_name: formData.provider_name,
                    display_name: formData.display_name || formData.provider_name,
                    api_key: formData.api_key || null,
                    api_secret: formData.api_secret || null,
                    is_enabled: formData.is_enabled,
                    notes: formData.notes || null,
                    additional_config: additional_config
                })
            });

            if (response.ok) {
                const newIntegration = await response.json();
                setIntegrations(prev => [...prev, newIntegration]);
                setExpandedCategories(prev => ({ ...prev, [formData.category]: true }));
                setShowAddModal(false);
                toast.success('Integration added successfully');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to add integration');
            }
        } catch (error) {
            console.error('Failed to add integration:', error);
            toast.error('Failed to add integration');
        } finally {
            setFormSaving(false);
        }
    };

    const handleUpdateIntegration = async () => {
        if (!selectedIntegration) return;

        setFormSaving(true);
        try {
            // Build additional_config for email category
            let additional_config = null;
            if (selectedIntegration.category === 'email') {
                additional_config = {
                    mode: formData.email_mode,
                    from_email: formData.from_email || null,
                    reply_to_email: formData.reply_to_email || null
                };
            }

            const response = await fetch(
                `${API_URL}/api/integrations/${selectedIntegration.integration_id}`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        display_name: formData.display_name,
                        notes: formData.notes || null,
                        additional_config: additional_config
                    })
                }
            );

            if (response.ok) {
                const updated = await response.json();
                setIntegrations(prev =>
                    prev.map(int =>
                        int.integration_id === selectedIntegration.integration_id
                            ? updated
                            : int
                    )
                );
                setShowEditModal(false);
                toast.success('Integration updated');
            } else {
                toast.error('Failed to update integration');
            }
        } catch (error) {
            console.error('Failed to update integration:', error);
            toast.error('Failed to update integration');
        } finally {
            setFormSaving(false);
        }
    };

    const handleUpdateCredentials = async () => {
        if (!selectedIntegration) return;

        if (!formData.api_key) {
            toast.error('API Key is required');
            return;
        }

        setFormSaving(true);
        try {
            const response = await fetch(
                `${API_URL}/api/integrations/${selectedIntegration.integration_id}/credentials`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        api_key: formData.api_key,
                        api_secret: formData.api_secret || null
                    })
                }
            );

            if (response.ok) {
                setIntegrations(prev =>
                    prev.map(int =>
                        int.integration_id === selectedIntegration.integration_id
                            ? { ...int, api_key_configured: true, api_secret_configured: !!formData.api_secret }
                            : int
                    )
                );
                setShowCredentialsModal(false);
                toast.success('Credentials updated (encrypted)');
            } else {
                toast.error('Failed to update credentials');
            }
        } catch (error) {
            console.error('Failed to update credentials:', error);
            toast.error('Failed to update credentials');
        } finally {
            setFormSaving(false);
        }
    };

    const handleDeleteIntegration = async () => {
        if (!selectedIntegration) return;

        setFormSaving(true);
        try {
            const response = await fetch(
                `${API_URL}/api/integrations/${selectedIntegration.integration_id}`,
                {
                    method: 'DELETE',
                    credentials: 'include'
                }
            );

            if (response.ok) {
                setIntegrations(prev =>
                    prev.filter(int => int.integration_id !== selectedIntegration.integration_id)
                );
                setShowDeleteModal(false);
                toast.success('Integration deleted');
            } else {
                toast.error('Failed to delete integration');
            }
        } catch (error) {
            console.error('Failed to delete integration:', error);
            toast.error('Failed to delete integration');
        } finally {
            setFormSaving(false);
        }
    };

    const getIntegrationsByCategory = (category) => {
        return integrations.filter(int => int.category === category);
    };

    const IntegrationCard = ({ integration }) => {
        const healthConfig = HEALTH_STATUS_CONFIG[integration.health_status] || HEALTH_STATUS_CONFIG.unknown;
        const HealthIcon = healthConfig.icon;
        const isTesting = testingId === integration.integration_id;
        const isToggling = togglingId === integration.integration_id;

        return (
            <div
                className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 hover:border-zinc-600 transition-colors"
                data-testid={`integration-card-${integration.integration_id}`}
            >
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h4 className="text-white font-medium truncate">
                                {integration.display_name || integration.provider_name}
                            </h4>
                            <Badge
                                variant="outline"
                                className={`text-xs ${healthConfig.color} border-current`}
                            >
                                <HealthIcon className="w-3 h-3 mr-1" />
                                {healthConfig.label}
                            </Badge>
                        </div>
                        <p className="text-zinc-500 text-sm mt-1">{integration.provider_name}</p>
                        
                        <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                            <span className="flex items-center gap-1">
                                <Key className="w-3 h-3" />
                                {integration.api_key_configured ? 'Key configured' : 'No key'}
                            </span>
                            {integration.last_tested_at && (
                                <span>
                                    Tested: {new Date(integration.last_tested_at).toLocaleDateString()}
                                </span>
                            )}
                            {/* Email mode indicator */}
                            {integration.category === 'email' && integration.additional_config?.mode && (
                                <Badge 
                                    variant="outline"
                                    className={`text-xs ${
                                        integration.additional_config.mode === 'api' 
                                            ? 'text-green-400 border-green-400' 
                                            : 'text-amber-400 border-amber-400'
                                    }`}
                                >
                                    {integration.additional_config.mode === 'api' ? 'API Mode' : 'Draft Mode'}
                                </Badge>
                            )}
                        </div>
                        
                        {/* Email from address */}
                        {integration.category === 'email' && integration.additional_config?.from_email && (
                            <p className="text-zinc-400 text-xs mt-2">
                                From: {integration.additional_config.from_email}
                            </p>
                        )}
                        
                        {integration.last_error && (
                            <p className="text-red-400 text-xs mt-2 truncate" title={integration.last_error}>
                                {integration.last_error}
                            </p>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <Switch
                            checked={integration.is_enabled}
                            onCheckedChange={() => handleToggleEnabled(integration)}
                            disabled={isToggling}
                            data-testid={`toggle-integration-${integration.integration_id}`}
                        />
                    </div>
                </div>

                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-zinc-700">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTestConnection(integration)}
                        disabled={isTesting || !integration.api_key_configured}
                        className="text-zinc-400 hover:text-white"
                        data-testid={`test-btn-${integration.integration_id}`}
                    >
                        {isTesting ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                            <TestTube className="w-4 h-4 mr-1" />
                        )}
                        Test
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openCredentialsModal(integration)}
                        className="text-zinc-400 hover:text-white"
                        data-testid={`credentials-btn-${integration.integration_id}`}
                    >
                        <Key className="w-4 h-4 mr-1" />
                        Credentials
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(integration)}
                        className="text-zinc-400 hover:text-white"
                        data-testid={`edit-btn-${integration.integration_id}`}
                    >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteModal(integration)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-btn-${integration.integration_id}`}
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        );
    };

    const CategorySection = ({ categoryKey }) => {
        const config = CATEGORY_CONFIG[categoryKey];
        const categoryIntegrations = getIntegrationsByCategory(categoryKey);
        const isExpanded = expandedCategories[categoryKey];
        const Icon = config.icon;

        return (
            <div className="space-y-3">
                <button
                    onClick={() => toggleCategory(categoryKey)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg ${config.bgColor} border ${config.borderColor} hover:opacity-80 transition-opacity`}
                    data-testid={`category-header-${categoryKey}`}
                >
                    <div className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${config.color}`} />
                        <span className={`font-semibold uppercase tracking-wider text-sm ${config.color}`}>
                            {config.label}
                        </span>
                        <Badge variant="outline" className="text-zinc-400 border-zinc-600 ml-2">
                            {categoryIntegrations.length}
                        </Badge>
                    </div>
                    {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-zinc-400" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-zinc-400" />
                    )}
                </button>

                {isExpanded && (
                    <div className="space-y-3 pl-2">
                        {categoryIntegrations.length === 0 ? (
                            <div className="text-center py-6 text-zinc-500">
                                <p className="text-sm">No integrations configured</p>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openAddModal(categoryKey)}
                                    className="mt-2 text-zinc-400 hover:text-white"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add {config.label.slice(0, -1)}
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    {categoryIntegrations.map(integration => (
                                        <IntegrationCard
                                            key={integration.integration_id}
                                            integration={integration}
                                        />
                                    ))}
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openAddModal(categoryKey)}
                                    className="text-zinc-400 hover:text-white w-full border border-dashed border-zinc-700 hover:border-zinc-600"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Another
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-blue-500" data-testid="integration-registry">
                <CardHeader className="border-b border-zinc-800 pb-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2">
                                <Radio className="w-5 h-5 text-blue-500" />
                                Integration Registry
                            </CardTitle>
                            <CardDescription className="text-zinc-500 mt-1">
                                Manage API credentials securely. All keys are encrypted at rest.
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={fetchIntegrations}
                                className="text-zinc-400 hover:text-white"
                                data-testid="refresh-integrations-btn"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => openAddModal()}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                data-testid="add-integration-btn"
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                Add Integration
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                        </div>
                    ) : (
                        Object.keys(CATEGORY_CONFIG).map(categoryKey => (
                            <CategorySection key={categoryKey} categoryKey={categoryKey} />
                        ))
                    )}
                </CardContent>
            </Card>

            {/* Add Integration Modal */}
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Integration</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Configure a new third-party integration. API keys are encrypted before storage.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Category</Label>
                            <Select
                                value={formData.category}
                                onValueChange={(value) => setFormData({ ...formData, category: value, provider_name: '' })}
                            >
                                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                    <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700">
                                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                                        <SelectItem key={key} value={key} className="text-white hover:bg-zinc-700">
                                            {config.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {formData.category && (
                            <div className="space-y-2">
                                <Label>Provider</Label>
                                <Select
                                    value={formData.provider_name}
                                    onValueChange={(value) => setFormData({ ...formData, provider_name: value })}
                                >
                                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                        <SelectValue placeholder="Select provider" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-800 border-zinc-700">
                                        {CATEGORY_CONFIG[formData.category]?.providers.map(provider => (
                                            <SelectItem key={provider} value={provider} className="text-white hover:bg-zinc-700">
                                                {provider}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label>Display Name (optional)</Label>
                            <Input
                                value={formData.display_name}
                                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                                placeholder="e.g., Production OpenAI"
                                className="bg-zinc-800 border-zinc-700"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>API Key</Label>
                            <Input
                                type="password"
                                value={formData.api_key}
                                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                                placeholder="Enter API key (will be encrypted)"
                                className="bg-zinc-800 border-zinc-700"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>API Secret (optional)</Label>
                            <Input
                                type="password"
                                value={formData.api_secret}
                                onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                                placeholder="Enter API secret if required"
                                className="bg-zinc-800 border-zinc-700"
                            />
                        </div>

                        {/* Email-specific fields */}
                        {formData.category === 'email' && (
                            <>
                                <div className="border-t border-zinc-700 pt-4 mt-4">
                                    <Label className="text-orange-400 text-sm uppercase tracking-wider mb-3 block">
                                        Email Configuration
                                    </Label>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Email Mode</Label>
                                    <Select
                                        value={formData.email_mode}
                                        onValueChange={(value) => setFormData({ ...formData, email_mode: value })}
                                    >
                                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-800 border-zinc-700">
                                            <SelectItem value="draft" className="text-white hover:bg-zinc-700">
                                                Draft Mode (mailto: link)
                                            </SelectItem>
                                            <SelectItem value="api" className="text-white hover:bg-zinc-700">
                                                API Mode (direct send)
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-zinc-500">
                                        {formData.email_mode === 'draft' 
                                            ? 'Opens email draft in your mail client - no API key required'
                                            : 'Sends emails directly via API - requires valid API key'}
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label>From Email {formData.email_mode === 'api' && <span className="text-red-400">*</span>}</Label>
                                    <Input
                                        type="email"
                                        value={formData.from_email}
                                        onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                                        placeholder="noreply@yourdomain.com"
                                        className="bg-zinc-800 border-zinc-700"
                                    />
                                    <p className="text-xs text-zinc-500">
                                        The email address that will appear as sender
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label>Reply-To Email (optional)</Label>
                                    <Input
                                        type="email"
                                        value={formData.reply_to_email}
                                        onChange={(e) => setFormData({ ...formData, reply_to_email: e.target.value })}
                                        placeholder="dispatch@yourdomain.com"
                                        className="bg-zinc-800 border-zinc-700"
                                    />
                                </div>
                            </>
                        )}

                        <div className="flex items-center justify-between">
                            <Label>Enable on save</Label>
                            <Switch
                                checked={formData.is_enabled}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowAddModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddIntegration}
                            disabled={formSaving || !formData.category || !formData.provider_name}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {formSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Add Integration
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Integration Modal */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Integration</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Update integration details. Use "Credentials" button to update API keys.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Provider</Label>
                            <Input
                                value={formData.provider_name}
                                disabled
                                className="bg-zinc-800 border-zinc-700 opacity-50"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Display Name</Label>
                            <Input
                                value={formData.display_name}
                                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                                className="bg-zinc-800 border-zinc-700"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Input
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Optional notes"
                                className="bg-zinc-800 border-zinc-700"
                            />
                        </div>

                        {/* Email-specific fields in Edit Modal */}
                        {selectedIntegration?.category === 'email' && (
                            <>
                                <div className="border-t border-zinc-700 pt-4 mt-4">
                                    <Label className="text-orange-400 text-sm uppercase tracking-wider mb-3 block">
                                        Email Configuration
                                    </Label>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Email Mode</Label>
                                    <Select
                                        value={formData.email_mode}
                                        onValueChange={(value) => setFormData({ ...formData, email_mode: value })}
                                    >
                                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-800 border-zinc-700">
                                            <SelectItem value="draft" className="text-white hover:bg-zinc-700">
                                                Draft Mode (mailto: link)
                                            </SelectItem>
                                            <SelectItem value="api" className="text-white hover:bg-zinc-700">
                                                API Mode (direct send)
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>From Email</Label>
                                    <Input
                                        type="email"
                                        value={formData.from_email}
                                        onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                                        placeholder="noreply@yourdomain.com"
                                        className="bg-zinc-800 border-zinc-700"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Reply-To Email (optional)</Label>
                                    <Input
                                        type="email"
                                        value={formData.reply_to_email}
                                        onChange={(e) => setFormData({ ...formData, reply_to_email: e.target.value })}
                                        placeholder="dispatch@yourdomain.com"
                                        className="bg-zinc-800 border-zinc-700"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowEditModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpdateIntegration}
                            disabled={formSaving}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {formSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Update Credentials Modal */}
            <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle>Update Credentials</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Enter new API credentials. These will be encrypted before storage.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                            <p className="text-amber-300 text-sm">
                                For security, existing credentials are not shown. Enter new values to update.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label>API Key</Label>
                            <Input
                                type="password"
                                value={formData.api_key}
                                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                                placeholder="Enter new API key"
                                className="bg-zinc-800 border-zinc-700"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>API Secret (optional)</Label>
                            <Input
                                type="password"
                                value={formData.api_secret}
                                onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                                placeholder="Enter new API secret"
                                className="bg-zinc-800 border-zinc-700"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowCredentialsModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpdateCredentials}
                            disabled={formSaving || !formData.api_key}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {formSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Update Credentials
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Integration</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Are you sure you want to delete this integration? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        {selectedIntegration && (
                            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                                <p className="text-white font-medium">
                                    {selectedIntegration.display_name || selectedIntegration.provider_name}
                                </p>
                                <p className="text-zinc-400 text-sm mt-1">
                                    {selectedIntegration.provider_name} • {CATEGORY_CONFIG[selectedIntegration.category]?.label}
                                </p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteIntegration}
                            disabled={formSaving}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {formSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Delete Integration
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
